/**
 * Game mode arena (plan 47, Phases 2 + 4): the pure selectors behind the
 * translate-it stamps, the score pill, the retry / skip controls and the
 * end-of-game summary. The stamp rule itself is `gameTurnStampOf`
 * (`@orborix/shared-types`) — the live thread, the "Continuar" history seeded
 * into it and the history's session detail all go through it, so a stamp and
 * the correction chip under it can never disagree: they read the same data.
 * It reads only the chips that touch the sentence the meaning check judged
 * (`gameAttempt.judgedText`): a chip on what the learner said after the
 * translation is shown, but does not demote the stamp.
 *
 * What a selector here adds on top of `gameTurnStampOf` is only the LIVE timing:
 * a learner bubble of the game is "judging" while its meaning verdict
 * (`gameAttempt`, the `game.round` verdict event) or its corrections verdict
 * (`corrections.done`) is still missing, until `GAME_STAMP_GRACE_MS` passed —
 * then it stamps with whatever arrived. A bubble whose meaning verdict never
 * arrives, or arrives unknown (`null`: no check for the session, a failed
 * one), gets NO stamp: `gameTurnStampOf` cannot tell an attempt from chat without
 * it. Everything is derived on each render, so a chip that lands late
 * re-stamps the bubble by itself.
 *
 * Which bubbles are attempts: the hook (`useGameArena`) tags every learner
 * bubble that appears while translate-it is the active seed with the id of the
 * game RUN it belongs to (a run = from picking translate-it to leaving it).
 * A bubble that carries a `gameAttempt` is an attempt too (a seeded historical
 * bubble, or a verdict that arrived for an untagged bubble).
 */
import {
  GAME_SEEN,
  gameJudgedCorrections,
  gameScoreOf,
  gameTurnStampOf,
  type GameScore,
  type GameScoreEntry,
  type GameStamp,
} from '@orborix/shared-types';
import type { VoiceMessage, VoiceMessageCorrection } from '@orborix/voice-client';
import type { TranscriptTurn } from '../../lib/sessionsApi.js';

/** A bubble's stamp as the thread shows it: a final stamp, or still judging. */
export type GameBubbleStamp = GameStamp | 'judging';

/**
 * The stamp of ONE learner bubble of the game.
 *
 * - A historical bubble (seeded from the transcript) is always final: its
 *   meaning verdict is the persisted one (`gameAttempt.verdict`), its
 *   corrections verdict the persisted `historicalTurn.correctionsVerdict`.
 * - A live bubble judges until its meaning verdict arrives (or the grace
 *   ends: then no stamp). With it, it is final once the corrections verdict
 *   arrived, once `graceOver`, or as soon as nothing still missing could
 *   change it: no stamp (a `not-attempt` or unknown meaning) or an
 *   already-certain `miss` (a `wrong` meaning, or a communication-breaking
 *   chip — the top of the table).
 *
 * `null` = no stamp (not an attempt, meaning unknown, or nothing known after
 * the grace).
 */
export function bubbleGameStamp(m: VoiceMessage, graceOver: boolean): GameBubbleStamp | null {
  if (m.who !== 'user') return null;
  const meaning = m.gameAttempt?.verdict ?? null;
  const formStatus = m.historical
    ? (m.historicalTurn?.correctionsVerdict ?? null)
    : (m.correctionsVerdict?.status ?? null);
  const stamp = gameTurnStampOf({
    meaning,
    turnText: m.text,
    judgedText: m.gameAttempt?.judgedText ?? null,
    corrections: m.corrections ?? [],
    correctionsVerdict: formStatus,
  });
  if (m.historical || graceOver) return stamp;
  if (m.gameAttempt === undefined) return 'judging';
  if (m.correctionsVerdict !== undefined || stamp === null || stamp === 'miss') return stamp;
  return 'judging';
}

/**
 * The live bubbles {@link selectGameStamps} may show as "judging": the ones
 * tagged with a run, and any live learner bubble carrying a `gameAttempt` (a
 * verdict that reached an untagged bubble — e.g. one that appeared in the
 * render the game started, or right after Salir). Each needs its grace timer,
 * or a session without the corrector would leave it judging forever.
 */
export function selectGraceIndexes(
  messages: ReadonlyArray<VoiceMessage>,
  runs: ReadonlyMap<number, number>,
): ReadonlySet<number> {
  const out = new Set<number>(runs.keys());
  messages.forEach((m, i) => {
    if (m.who === 'user' && !m.historical && m.gameAttempt !== undefined) out.add(i);
  });
  return out;
}

/**
 * Index → stamp of every game attempt of the thread that shows one (judging or
 * final). `runs` tags the live bubbles sent during translate-it (index → run
 * id); `graceOver` holds the indexes whose grace period ended.
 */
export function selectGameStamps(
  messages: ReadonlyArray<VoiceMessage>,
  runs: ReadonlyMap<number, number>,
  graceOver: ReadonlySet<number>,
): Map<number, GameBubbleStamp> {
  const out = new Map<number, GameBubbleStamp>();
  messages.forEach((m, i) => {
    if (m.who !== 'user') return;
    if (!runs.has(i) && m.gameAttempt === undefined) return;
    const stamp = bubbleGameStamp(m, graceOver.has(i));
    if (stamp !== null) out.set(i, stamp);
  });
  return out;
}

/** The message indexes tagged with `run`, in thread order. */
export function runAttemptIndexes(runs: ReadonlyMap<number, number>, run: number): number[] {
  const out: number[] = [];
  for (const [index, r] of runs) if (r === run) out.push(index);
  return out.sort((a, b) => a - b);
}

/**
 * The score of a run from its attempts' stamps (`gameScoreOf`): a bubble still
 * judging, or without a stamp, neither counts nor breaks the streak yet.
 * `seenAfter` = the message index after which each «No sé» of the run was
 * pressed (plan 47 Round 2): a «vista» entry placed there in the run's order —
 * it counts as `seen` and ends the streak.
 */
export function gameRunScore(
  indexes: ReadonlyArray<number>,
  stamps: ReadonlyMap<number, GameBubbleStamp>,
  seenAfter: ReadonlyArray<number> = [],
): GameScore {
  const entries: Array<{ at: number; entry: GameScoreEntry }> = indexes.map((i) => {
    const s = stamps.get(i);
    return { at: i, entry: s === undefined || s === 'judging' ? null : s };
  });
  for (const after of seenAfter) entries.push({ at: after + 0.5, entry: GAME_SEEN });
  entries.sort((a, b) => a.at - b.at);
  return gameScoreOf(entries.map((e) => e.entry));
}

/** What the learner can ask for from an attempt bubble (plan 47 § Retry and skip). */
export type GameAttemptAction = 'retry' | 'skip';

/**
 * The control an attempt offers by its stamp, at a FIRST try only: «↺
 * Repetirla» on a `close`, «Saltear →» on a `miss`. A second try (a rematch,
 * the coach's second shot) offers nothing: after it the game continues with a
 * fresh sentence (plan 47 § Retry and skip) — no endless rematches. The
 * caller shows it only on the newest attempt of the running game — `retry`
 * re-serves exactly the last judged challenge.
 *
 * «Saltear» only where the coach really gave the second shot: a Jev `wrong`
 * meaning (the neutral reaction's one branch that re-serves the same
 * sentence). A `miss` that comes from a communication-breaking chip got a
 * FRESH sentence from the coach, which the learner has not tried yet:
 * skipping would throw that one away. That miss offers the rematch instead,
 * like a `close`.
 */
export function gameAttemptActionFor(
  m: VoiceMessage,
  stamp: GameBubbleStamp | undefined,
): GameAttemptAction | null {
  if (stamp !== 'close' && stamp !== 'miss') return null;
  if ((m.gameAttempt?.attemptNo ?? 1) > 1) return null;
  return stamp === 'miss' && m.gameAttempt?.verdict === 'wrong' ? 'skip' : 'retry';
}

/**
 * The learner's sentence with its grammar corrections applied (each `said`
 * replaced by its `fix`, first occurrence, case-insensitive). `null` when no
 * correction's `said` is found in the text (nothing honest to show).
 */
export function correctedFormOf(
  text: string,
  corrections: ReadonlyArray<Pick<VoiceMessageCorrection, 'said' | 'fix'>>,
): string | null {
  let out = text;
  let applied = false;
  for (const c of corrections) {
    if (c.said.trim() === '') continue;
    const at = out.toLowerCase().indexOf(c.said.toLowerCase());
    if (at === -1) continue;
    out = out.slice(0, at) + c.fix + out.slice(at + c.said.length);
    applied = true;
  }
  return applied ? out : null;
}

/** One attempt that was not clean, as the end-of-game summary reviews it. */
export interface GameSummaryReview {
  index: number;
  stamp: 'close' | 'miss';
  /** What the learner said. */
  said: string;
  /** The Spanish challenge it answered, when known. */
  challenge: string | null;
  /**
   * The sentence with the corrections that touch the judged sentence applied
   * (the ones the stamp read); `null` = no corrected form.
   */
  corrected: string | null;
  /**
   * The server's anchor of the attempt, for «Practicar estas N»: its turn
   * and, for one from before a resume into a new session row, that session
   * (`sessionId: null` = the live session). `null` = no turn learned.
   */
  turn: { sessionId: string | null; turnIndex: number } | null;
  /** Grammar corrections shown on the attempt (its chips). */
  corrections: number;
}

/** A mark of the summary's stamp row: an attempt's stamp, or a «No sé» («vista»). */
export type GameSummaryMark = GameBubbleStamp | typeof GAME_SEEN;

/** A challenge the learner revealed with «No sé», as the summary reviews it. */
export interface GameSummaryReveal {
  /** The message index after which «No sé» was pressed (its place in the run). */
  after: number;
  challenge: string;
  /** The revealed version; `null` = still loading, or the generation failed. */
  solution: string | null;
}

/** The end-of-game summary card (plan 47 Phase 4; «vista» since Round 2). */
export interface GameSummary {
  /** Distinct challenges (by sentence) the run's attempts answered or revealed. */
  rounds: number;
  score: GameScore;
  /** Every attempt's stamp and every «vista» in run order (judging ones included: they may still land). */
  marks: ReadonlyArray<GameSummaryMark>;
  /** The attempts that were not clean (`close` / `miss`), with their corrected form. */
  review: ReadonlyArray<GameSummaryReview>;
  /** The revealed challenges, in run order. */
  revealed: ReadonlyArray<GameSummaryReveal>;
}

function challengeKey(challenge: string): string {
  return challenge.toLowerCase().replace(/[¿?¡!.,;:…"'«»“”]/gu, '').trim();
}

/** The summary of a run's attempts (indexes in thread order) and its «No sé». */
export function gameSummaryOf(
  messages: ReadonlyArray<VoiceMessage>,
  indexes: ReadonlyArray<number>,
  stamps: ReadonlyMap<number, GameBubbleStamp>,
  reveals: ReadonlyArray<GameSummaryReveal> = [],
): GameSummary {
  const stamped = indexes.filter((i) => stamps.has(i));
  // Distinct challenges by their sentence, not by `round`: the server's
  // `round` is the session's count of distinct challenges at that moment, so
  // a ↺ rematch of an earlier sentence carries the number of the round it
  // interrupted — counting numbers would add a round that was never played.
  const rounds = new Set<string>();
  const review: GameSummaryReview[] = [];
  for (const i of stamped) {
    const m = messages[i];
    if (m === undefined) continue;
    const challenge = m.gameAttempt?.challenge;
    if (challenge) rounds.add(challengeKey(challenge));
    const stamp = stamps.get(i);
    if (stamp === 'close' || stamp === 'miss') {
      review.push({
        index: i,
        stamp,
        said: m.text,
        challenge: m.gameAttempt?.challenge ?? null,
        corrected: correctedFormOf(
          m.text,
          gameJudgedCorrections(m.text, m.gameAttempt?.judgedText ?? null, m.corrections ?? []),
        ),
        turn: m.previousSession
          ? m.previousSession.turnIndex === null
            ? null
            : { sessionId: m.previousSession.sessionId, turnIndex: m.previousSession.turnIndex }
          : m.turnIndex !== undefined
            ? { sessionId: null, turnIndex: m.turnIndex }
            : null,
        corrections: m.corrections?.length ?? 0,
      });
    }
  }
  for (const r of reveals) rounds.add(challengeKey(r.challenge));
  const marks: Array<{ at: number; mark: GameSummaryMark }> = stamped.map((i) => ({
    at: i,
    mark: stamps.get(i)!,
  }));
  for (const r of reveals) marks.push({ at: r.after + 0.5, mark: GAME_SEEN });
  marks.sort((a, b) => a.at - b.at);
  return {
    rounds: rounds.size,
    score: gameRunScore(
      stamped,
      stamps,
      reveals.map((r) => r.after),
    ),
    marks: marks.map((m) => m.mark),
    review,
    revealed: [...reveals].sort((a, b) => a.after - b.after),
  };
}

/**
 * Turn index → stamp of every past translate-it turn of a session transcript
 * (`turns[].gameRound`, migrations 0419 + 0421): the same `gameTurnStampOf`
 * call as live, on the persisted meaning verdict and judged sentence, the
 * turn's corrections (the mixed list: pronunciation rows are skipped by the
 * function) and its persisted corrections verdict. Turns without a round, or
 * without a stamp, are absent.
 */
export function selectTranscriptGameStamps(
  turns: ReadonlyArray<TranscriptTurn>,
): ReadonlyMap<number, GameStamp> {
  const out = new Map<number, GameStamp>();
  for (const t of turns) {
    if (!t.gameRound || !t.userText) continue;
    const stamp = gameTurnStampOf({
      meaning: t.gameRound.meaningVerdict,
      turnText: t.userText,
      judgedText: t.gameRound.judgedText,
      corrections: t.corrections,
      correctionsVerdict: t.correctionsVerdict,
    });
    if (stamp !== null) out.set(t.turnIndex, stamp);
  }
  return out;
}

/* -------------------------------------------------------------------------- */
/* Game runs (which live bubbles belong to which translate-it game)            */
/* -------------------------------------------------------------------------- */

/** A translate-it game in progress: from picking the seed to leaving it. */
export interface GameRun {
  id: number;
  /** `messages.length` when it started: its attempts come after. */
  startLen: number;
  /**
   * The live round (`useVoiceSession().gameRound`, by identity) when it
   * started: a round left over from an earlier run of the same connection is
   * not this run's challenge.
   */
  roundAtStart: unknown;
}

/** A finished run: its summary card goes after message `endLen - 1`. */
export interface EndedGameRun {
  id: number;
  endLen: number;
}

/** The runs state the arena hook keeps (advanced by {@link advanceGameRuns}). */
export interface GameRunsState {
  /** `messages.length` already looked at for tagging. */
  seenLen: number;
  /** Learner bubble index → run id, for every bubble sent during a run. */
  tags: ReadonlyMap<number, number>;
  current: GameRun | null;
  ended: ReadonlyArray<EndedGameRun>;
  nextId: number;
}

export const INITIAL_GAME_RUNS: GameRunsState = {
  seenLen: 0,
  tags: new Map(),
  current: null,
  ended: [],
  nextId: 1,
};

/**
 * One step of the runs state, from what the screen sees now. Returns `prev`
 * itself when nothing changed (the hook stores it during render only then).
 *
 * - New live learner bubbles are tagged with the run that was going on when
 *   they appeared (before a seed change seen in the same step is applied).
 * - A run starts when translate-it becomes the active seed of a live session,
 *   and ends when the seed changes (Salir, another seed) or the session ends
 *   while playing — its summary card goes after the thread's last message.
 * - `seenLen` follows `messages.length` down too (a reseeded thread); tags
 *   past the new length are dropped.
 */
export function advanceGameRuns(
  prev: GameRunsState,
  now: {
    messages: ReadonlyArray<VoiceMessage>;
    playingTranslateIt: boolean;
    ended: boolean;
    gameRound: unknown;
  },
): GameRunsState {
  let next = prev;
  const len = now.messages.length;
  if (len !== prev.seenLen) {
    const tags = new Map(prev.tags);
    if (len < prev.seenLen) {
      for (const i of tags.keys()) if (i >= len) tags.delete(i);
    } else if (prev.current) {
      for (let i = prev.seenLen; i < len; i += 1) {
        const m = now.messages[i];
        if (m && m.who === 'user' && !m.historical) tags.set(i, prev.current.id);
      }
    }
    next = { ...next, seenLen: len, tags };
  }
  const shouldPlay = now.playingTranslateIt && !now.ended;
  if (next.current && !shouldPlay) {
    next = {
      ...next,
      ended: [...next.ended, { id: next.current.id, endLen: len }],
      current: null,
    };
  } else if (!next.current && shouldPlay) {
    next = {
      ...next,
      current: { id: next.nextId, startLen: len, roundAtStart: now.gameRound },
      nextId: next.nextId + 1,
    };
  }
  return next;
}
